import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { LitElement, html, TemplateResult } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { BreachPathSideMenuDataType } from '../types';
import { BreachPathDetailsTabPanels, BreachPathEvents } from '../../common/types'
import {
  nameRenderer,
  cveRenderer,
  mainResourceNameRenderer,
  affectedResourcesRenderer,
  lastScannedOnRenderer,
  firstFoundOnRenderer,
  resourceNameRenderer,
  resourceIdRenderer,
  resourceTypeRenderer,
  platformRenderer,
  cloudAccountRenderer,
  regionRenderer,
  riskScoreRenderer,
} from '../utils/customDataRenderer';
import { handleWatchlistChangeButtonClick, renderButtonGroup, renderSideMenuWatchlist } from 'apps/webcomponents/src/riskManagement/sideMenus/common/common';
import { getWatchlistButtonTextBasedOnState } from 'apps/webcomponents/src/riskManagement/common/utils';
import { GzButtonWebComponent } from 'apps/webcomponents/src/index.ui';

/**
 * @summary Breach Path side menu web component
 * @tag breach-path-side-menu
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('breach-path-side-menu')
export class BreachPathSideMenuWebComponent extends LitElement {
  @property({ type: Object, attribute: false })
  breachPathData: BreachPathSideMenuDataType = {} as BreachPathSideMenuDataType;

  @property({ type: Boolean, attribute: false })
  loading = false;

  @query('#watchlist-button')
  private readonly watchlistButton!: GzButtonWebComponent;

  @property({ type: Object, attribute: false })
  item = {} as any;

  static get styles() {
    return styles;
  }

  render(): TemplateResult {
    if (this.loading) {
      return this.renderLoading();
    }

    return html`
      <div class="header mt-6">
        <gz-title fontSize="${FontSizes.HEADING_5}" class="mb-2 pr-2">
          ${nameRenderer(this.breachPathData)}
        </gz-title>
      </div>
      <div class="content">
        ${this.renderGeneralSection()}
        ${this.renderMainResourceSection()}
      </div>
      <div class="footer">
        <gz-button @click="${e => this.handleViewDetailsButtonClick(e, this.breachPathData)}">
          ${translateFn('View Details')}
        </gz-button>
        <gz-button @click="${e => this.handleViewResourcesButtonClick(e, this.breachPathData)}" type="quiet">
          ${translateFn('View Resources')}
        </gz-button>
      </div>
    `;
  }

  private renderLoading(): TemplateResult {
    return html`
      <gz-loader rows="5" style="padding-right: 16px; padding-top: 48px;"></gz-loader>
    `;
  }

  public changeWatchlistButtonState(isLoading: boolean) {
    this.watchlistButton.setLoadingState(isLoading);
  }

  private renderGeneralSection(): TemplateResult {
    return html`
      <div class="section pb-2" data-section="general">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="section-title text-uppercase mb-2"
        >
          ${translateFn('General')}
        </gz-title>
        <gz-labeled-content label="${translateFn('CVEs')}">
          ${cveRenderer(this.breachPathData)}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Main resource name')}">
          ${mainResourceNameRenderer(this.breachPathData)}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Affected resources')}">
          ${affectedResourcesRenderer(this.breachPathData)}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Last scanned on')}">
          ${lastScannedOnRenderer(this.breachPathData)}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Created on')}">
          ${firstFoundOnRenderer(this.breachPathData)}
        </gz-labeled-content>
        ${renderSideMenuWatchlist(this.breachPathData.watchlist)}
        ${renderButtonGroup([
          {
            text: getWatchlistButtonTextBasedOnState(this.breachPathData.watchlist),
            handler: () => {
              this.item = this.breachPathData;
              handleWatchlistChangeButtonClick.bind(this)();
            },
            id: 'watchlist-button',
          },
        ])}
      </div>
    `;
  }

  private renderMainResourceSection(): TemplateResult {
    return html`
      <div class="section pt-2 pb-1" data-section="main-resource">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="section-title text-uppercase mb-2"
        >
          ${translateFn('Main resource')}
        </gz-title>
        <gz-labeled-content label="${translateFn('Resource name')}">
          ${resourceNameRenderer(this.breachPathData)}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Risk score')}">
          ${riskScoreRenderer(this.breachPathData)}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Resource ID')}">
          ${resourceIdRenderer(this.breachPathData)}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Resource type')}">
          ${resourceTypeRenderer(this.breachPathData)}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Platform')}">
          ${platformRenderer(this.breachPathData)}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Cloud account')}">
          ${cloudAccountRenderer(this.breachPathData)}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Region')}">
          ${regionRenderer(this.breachPathData)}
        </gz-labeled-content>
      </div>
    `;
  }

  private handleViewDetailsButtonClick = (e, data) => {
    this.dispatchEvent(new CustomEvent(BreachPathEvents.VIEW_DETAILS, {
      detail: {
        data,
        extraDetails: {
          tab: BreachPathDetailsTabPanels.GRAPH,
        },
      },
      bubbles: true,
      cancelable: true,
      composed: true,
    }))
  }

  private handleViewResourcesButtonClick = (e, data) => {
    this.dispatchEvent(new CustomEvent(BreachPathEvents.VIEW_RESOURCES, {
      detail: {
        data,
        extraDetails: {
          tab: BreachPathDetailsTabPanels.RESOURCES,
        },
      },
      bubbles: true,
      cancelable: true,
      composed: true,
    }))
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'breach-path-side-menu': BreachPathSideMenuWebComponent;
  }
}
